import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip, message } from "antd";
import moment from "moment/moment";
import DataParser from "../../../utlits/Dataparser";
import SessionSettingsService from "../../../Services/SessionSettingsService";
import { Helmet } from "react-helmet";
import secureLocalStorage from "react-secure-storage";
import SocketService from "../../../Services/SocketService";

import StatusBadge from "../../Reuseable/StatusBadge";
import TableSkeleton from "../../Reuseable/TableSkeleton";

const SessionManagement = () => {
    const navigate = useNavigate();
    const [settingsList, setSettingsList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [userPermissions, setuserPermissions] = useState([]);

    // Create/Edit/Delete State
    const [timeoutMinutes, setTimeoutMinutes] = useState("");
    const [editID, setEditID] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const companyId = DataParser.CompanyId();

    const getSettings = () => {
        if (!companyId) return;
        setIsLoading(true);
        SessionSettingsService.getSessionSettings(companyId)
            .then((res) => {
                // res is already res.data due to httpService implementation
                if (res.Good) {
                    setSettingsList(res.data || []);
                }
            })
            .catch((err) => {
                console.error("Error fetching session settings:", err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getSettings();
        const upermission = DataParser.CompanyUserPermissions();
        setuserPermissions(upermission || []);
    }, [companyId]);

    // 🟢 Socket Integration
    useEffect(() => {
        SocketService.connect();
        const handleUpdate = (data) => {
            if (Number(data.company_id) === Number(companyId)) {
                getSettings();
            }
        };
        SocketService.on("session_settings_update", handleUpdate);
        return () => SocketService.off("session_settings_update", handleUpdate);
    }, [companyId]);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!timeoutMinutes || timeoutMinutes <= 0) {
            setError("Please enter a valid time in minutes");
            return;
        }

        setSubmitting(true);
        try {
            const data = {
                company_id: companyId,
                timeout_minutes: parseInt(timeoutMinutes)
            };

            const res = await SessionSettingsService.createSessionSetting(data);

            if (res.Good) {
                message.success("Settings created successfully!");
                const trigger = document.getElementById("SessionModalCreateTrigger");
                if (trigger) trigger.click();
                getSettings();
                clearCreateState();
            } else {
                message.error(res.data || "Action failed");
            }
        } catch (err) {
            message.error(err.response?.data?.data || "Process failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!timeoutMinutes || timeoutMinutes <= 0) {
            setError("Please enter a valid time in minutes");
            return;
        }

        setSubmitting(true);
        try {
            const data = {
                timeout_minutes: parseInt(timeoutMinutes)
            };

            const res = await SessionSettingsService.updateSessionSetting(editID, data);

            if (res.Good) {
                message.success("Settings updated successfully!");
                const trigger = document.getElementById("SessionModalEditTrigger");
                if (trigger) trigger.click();
                getSettings();
                clearEditState();
            } else {
                message.error(res.data || "Action failed");
            }
        } catch (err) {
            message.error(err.response?.data?.data || "Process failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (item) => {
        try {
            const res = await SessionSettingsService.updateStatus(item.id);
            if (res.Good) {
                message.success("Status updated");
                getSettings();
            }
        } catch (err) {
            message.error("Status update failed");
        }
    };

    const handleOpenDelete = (e, item) => {
        e.preventDefault();
        setItemToDelete(item);
        const trigger = document.getElementById("openDeleteSessionTrigger");
        if (trigger) trigger.click();
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        const id = itemToDelete.id;
        
        // Optimistic UI update
        const newData = settingsList.filter((el) => el.id !== id);
        setSettingsList(newData);
        
        const closeBtn = document.getElementById("closeSessionDeleteModal");
        if (closeBtn) closeBtn.click();

        try {
            const res = await SessionSettingsService.deleteSessionSetting(id, companyId);
            if (res && res.data === "Un-Athunticated request") {
                secureLocalStorage.removeItem("adminInfo");
                secureLocalStorage.removeItem("admins");
                navigate("/");
            } else if (res && res.Good) {
                message.success("Session setting deleted successfully!");
            } else {
                message.error(res?.data || "Failed to delete session setting. Please try again!");
            }
            getSettings();
        } catch (err) {
            console.error("Delete error:", err);
            message.error("Failed to delete session setting. Please try again!");
            getSettings();
        }
        setItemToDelete(null);
    };

    const handleOpenUpdate = (e, item) => {
        e.preventDefault();
        setEditID(item.id);
        setTimeoutMinutes(item.timeout_minutes);
    };

    const clearCreateState = () => {
        setEditID(null);
        setTimeoutMinutes("");
        setError("");
    };

    const clearEditState = () => {
        setEditID(null);
        setTimeoutMinutes("");
        setError("");
    };

    return (
        <>
            <Helmet>
                <title>Session Management - Planit</title>
            </Helmet>
            <div className="main-content">
                <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 px-0">
                                <div className="d-flex align-items-end justify-content-end" style={{ padding: "0 0 12px 0" }}>
                                    <div className="d-flex flex-column align-items-end" style={{ gap: "6px" }}>
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <Link to={`/${DataParser.CompanyIdentifierRole()}/employee-attendance`}>Home</Link>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <Link to={`/${DataParser.CompanyIdentifierRole()}/settings`}>Settings</Link>
                                            </li>
                                            <li className="breadcrumb-item active">Session Management</li>
                                        </ol>

                                        {(settingsList.length === 0 && userPermissions.indexOf("sessionout-list") !== -1) && (
                                            <button
                                                className="btn btn-primary waves-effect waves-light"
                                                style={{ borderRadius: "5px" }}
                                                data-bs-toggle="modal"
                                                data-bs-target="#createSessionModal"
                                            >
                                                Create Session Setting
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-12 px-0">
                                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive listing-table-wrapper">
                                            <table className="table mb-0 custom-table listing-table">
                                                <thead>
                                                    <tr className="tableHeaderbg">
                                                        <th>Sr#</th>
                                                        <th>Timeout (Minutes)</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isLoading ? (
                                                        <TableSkeleton cols={4} rows={6} />
                                                    ) : (
                                                        <>
                                                            {settingsList.length > 0 ? (
                                                                settingsList.map((item, index) => (
                                                                    <tr key={item.id}>
                                                                        <td>{index + 1}</td>
                                                                        <td>{item.timeout_minutes} Mins</td>
                                                                        <td>
                                                                            {userPermissions.indexOf("sessionout-list") !== -1 || userPermissions.indexOf("session-management-edit") !== -1 ? (
                                                                                <StatusBadge 
                                                                                    item={item} 
                                                                                    api={SessionSettingsService.updateStatus} 
                                                                                    activeLabel="Active"
                                                                                    inactiveLabel="Deactive"
                                                                                />
                                                                            ) : (
                                                                                <>
                                                                                    {item.status === 1 ? (
                                                                                        <span className="badge bg-success text-white">Active</span>
                                                                                    ) : (
                                                                                        <span className="badge bg-danger text-white">Deactive</span>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </td>
                                                                        <td className="tableset">
                                                                            {userPermissions.indexOf("sessionout-list") !== -1 ? (
                                                                                <Link
                                                                                    to="#"
                                                                                    className="cursor-pointer iconscolor"
                                                                                    onClick={(e) => handleOpenDelete(e, item)}
                                                                                >
                                                                                    <Tooltip title="Delete Session Setting">
                                                                                        <i className="uil-trash iconscolor" />
                                                                                    </Tooltip>
                                                                                </Link>
                                                                            ) : (
                                                                                <span
                                                                                    className="uil-trash iconscolor text-muted"
                                                                                    style={{ cursor: "not-allowed", opacity: 0.5 }}
                                                                                    title="You don't have permission to delete"
                                                                                />
                                                                            )}
                                                                            {userPermissions.indexOf("sessionout-list") !== -1 ? (
                                                                                <Link
                                                                                    to="#"
                                                                                    className="cursor-pointer iconscolor"
                                                                                    onClick={(e) => handleOpenUpdate(e, item)}
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#editSessionModal"
                                                                                >
                                                                                    <Tooltip title="Edit Session Setting">
                                                                                        <i className="uil-pen iconscolor" />
                                                                                    </Tooltip>
                                                                                </Link>
                                                                            ) : (
                                                                                <span
                                                                                    className="uil-pen iconscolor text-muted"
                                                                                    style={{ cursor: "not-allowed", opacity: 0.5 }}
                                                                                    title="You don't have permission to edit"
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center">No records found.</td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hidden trigger for delete modal */}
                <div id="openDeleteSessionTrigger" data-bs-toggle="modal" data-bs-target="#deleteSessionModal" style={{ display: "none" }} />

                {/* Delete Confirmation Modal */}
                <div id="deleteSessionModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
                    <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
                        <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                            <div className="modal-header border-0 pb-1">
                                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Delete Session Setting</h5>
                                <button type="button" className="btn-close" id="closeSessionDeleteModal" data-bs-dismiss="modal" aria-label="Close" onClick={() => setItemToDelete(null)} />
                            </div>
                            <div className="modal-body pt-2 pb-2">
                                <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>Are you sure you want to delete this session setting?</p>
                                <p style={{ fontSize: "13px", color: "#ff4d4f", marginBottom: 0 }}>
                                    <i className="uil uil-exclamation-triangle me-1" />
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-1">
                                <button type="button" className="btn btn-danger waves-effect waves-light" onClick={confirmDelete} style={{ borderRadius: "5px", fontSize: "13px" }}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create modal content */}
                <div
                    id="createSessionModal"
                    className="modal fade"
                    tabIndex={-1}
                    role="dialog"
                    aria-labelledby="createModalLabel"
                    aria-hidden="true"
                    data-bs-keyboard="false"
                >
                    <form onSubmit={handleSubmit}>
                        <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
                            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                                <div className="modal-header border-0 pb-1">
                                    <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Create Session Setting</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        id="SessionModalCreateTrigger"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={clearCreateState}
                                    />
                                </div>
                                <div className="modal-body pt-2 pb-2">
                                    <div className="float-input-wrap">
                                        <input
                                            type="number"
                                            className={error ? "is-invalid" : ""}
                                            placeholder=" "
                                            value={timeoutMinutes}
                                            min="1"
                                            onChange={(e) => {
                                                setTimeoutMinutes(e.target.value);
                                                if (e.target.value !== "") setError("");
                                            }}
                                        />
                                        <label>Timeout (Minutes)*</label>
                                    </div>
                                    {error && <small className="text-danger mt-1 d-block">{error}</small>}
                                </div>
                                <div className="modal-footer border-0 pt-1">
                                    <button
                                        type="submit"
                                        className="btn btn-primary waves-effect waves-light"
                                        style={{ borderRadius: "5px", fontSize: "13px" }}
                                    >
                                        + Create Session Setting
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Edit modal content */}
                <div
                    id="editSessionModal"
                    className="modal fade"
                    tabIndex={-1}
                    role="dialog"
                    aria-labelledby="editModalLabel"
                    aria-hidden="true"
                    data-bs-keyboard="false"
                >
                    <form onSubmit={handleEditSubmit}>
                        <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
                            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                                <div className="modal-header border-0 pb-1">
                                    <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Edit Session Setting</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        id="SessionModalEditTrigger"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={clearEditState}
                                    />
                                </div>
                                <div className="modal-body pt-2 pb-2">
                                    <div className="float-input-wrap">
                                        <input
                                            type="number"
                                            className={error ? "is-invalid" : ""}
                                            placeholder=" "
                                            value={timeoutMinutes}
                                            min="1"
                                            onChange={(e) => {
                                                setTimeoutMinutes(e.target.value);
                                                if (e.target.value !== "") setError("");
                                            }}
                                        />
                                        <label>Timeout (Minutes)*</label>
                                    </div>
                                    {error && <small className="text-danger mt-1 d-block">{error}</small>}
                                </div>
                                <div className="modal-footer border-0 pt-1">
                                    <button
                                        type="submit"
                                        className="btn btn-primary waves-effect waves-light"
                                        style={{ borderRadius: "5px", fontSize: "13px" }}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default SessionManagement;
